'use client';

import { css, cx } from '@repo/pandacss/styled-system/css';
import { dynamicWithRetry } from '@repo/ui-v2/DynamicWithRetry';
import { Rating } from '@repo/ui-v2/StarRating/components/Rating';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChartLegend } from './ChartLegend';
import { ChartWrapper } from './ChartWrapper';
import { DEFAULT_COLORS } from './constants';
import {
  ARC_ACTIVE_CLASS,
  CONTAINER_ACTIVE_CLASS,
  LEGEND_ACTIVE_CLASS,
  LEGEND_ITEM_SELECTOR,
  PIE_ARC_SELECTOR,
  chartInteractionStyles,
} from './hoverSelectors';

type PieChartProps = { pieData: Array<{ value: number; fill: string }> };

const CategoriesPieChart = dynamicWithRetry<PieChartProps>(
  () => import('./CategoriesPieChart').then(m => ({ default: m.CategoriesPieChart })),
  { componentName: 'CategoriesPieChart', ssr: false },
);

export interface Category {
  label: string;
  tooltipLabel: string;
  value: number;
  color?: string;
}

type CategoriesProps = {
  items: Category[];
  overallRating?: number;
};

const CONTAINER_ID = 'star-rating-categories';

export function Categories({ items, overallRating = 5 }: CategoriesProps) {
  // Ref updates don't re-render; if the effect runs before the node exists, it never retries without state.
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const setContainerRef = useCallback((el: HTMLDivElement | null) => {
    setContainerEl(el);
  }, []);

  useEffect(() => {
    const container = containerEl;
    if (!container) return;

    const expectedArcCount = items.length;
    // Only drive hover highlighting on devices with a real pointer, so touch taps
    // (which open the accordion instead) don't leave a sticky hover state.
    const canHover =
      typeof window !== 'undefined' && window.matchMedia?.('(hover: hover)').matches === true;

    let legendItems: HTMLElement[] = [];
    let arcs: HTMLElement[] = [];
    let hoverIndex: number | null = null;
    // Touch devices have no hover, and non-accordion legend items (plain <div>, no
    // `[open]` state) can't be tracked via the accordion path — so a tap toggles the
    // index in this set to highlight it. Multiple may be selected; tapping again clears
    // just that one. Only used when `canHover` is false.
    const selectedIndices = new Set<number>();
    let arcCleanups: Array<() => void> = [];

    // Recompute marker classes from the current DOM. An arc is highlighted when its
    // accordion is open, or it's hovered (desktop), or it's tap-selected (touch). Several
    // can be active at once; the rest are dimmed. The active legend items get their
    // border highlight.
    function applyState() {
      if (!container) return;
      const active = new Set<number>();
      legendItems.forEach((el, i) => {
        if (el.hasAttribute('open')) active.add(i);
      });
      if (hoverIndex !== null) active.add(hoverIndex);
      selectedIndices.forEach(i => active.add(i));

      arcs.forEach((arc, i) => arc.classList.toggle(ARC_ACTIVE_CLASS, active.has(i)));
      legendItems.forEach((el, i) =>
        el.classList.toggle(LEGEND_ACTIVE_CLASS, i === hoverIndex || selectedIndices.has(i)),
      );
      container.classList.toggle(CONTAINER_ACTIVE_CLASS, active.size > 0);
    }

    function teardownArcs() {
      arcCleanups.forEach(fn => fn());
      arcCleanups = [];
    }

    function setupArcs() {
      if (!container) return;
      const pieArcs = Array.from(container.querySelectorAll<HTMLElement>(PIE_ARC_SELECTOR));
      // Same nodes already wired up — avoid teardown/re-attach churn.
      const sameNodes =
        arcs.length === pieArcs.length && arcs.every((node, i) => node === pieArcs[i]);
      if (arcs.length && sameNodes) return;

      teardownArcs();
      arcs = pieArcs;
      if (arcs.length !== expectedArcCount) return;

      arcs.forEach((arc, index) => {
        const legendItem = legendItems[index];
        const isDetails = legendItem?.tagName === 'DETAILS';

        // Hover is desktop-only: on hover, highlight the arc and open its accordion;
        // on leave, close it and clear the highlight.
        const onEnter = () => {
          if (!canHover) return;
          hoverIndex = index;
          if (isDetails && !legendItem.hasAttribute('open')) {
            legendItem.setAttribute('open', '');
          }
          applyState();
        };
        const onLeave = () => {
          if (!canHover) return;
          if (hoverIndex === index) {
            hoverIndex = null;
          }
          if (isDetails && legendItem.hasAttribute('open')) {
            legendItem.removeAttribute('open');
          }
          applyState();
        };
        // Note: arcs intentionally have no touch handler. Tapping an arc on touch
        // devices is a no-op (avoids laggy re-render churn); selection there is driven
        // by tapping legend items instead.
        arc.addEventListener('mouseenter', onEnter);
        arc.addEventListener('mouseleave', onLeave);
        arcCleanups.push(() => {
          arc.removeEventListener('mouseenter', onEnter);
          arc.removeEventListener('mouseleave', onLeave);
        });
      });

      applyState();
    }

    // Legend items are server-rendered, so wire them once up front.
    legendItems = Array.from(container.querySelectorAll<HTMLElement>(LEGEND_ITEM_SELECTOR));
    const legendCleanups: Array<() => void> = [];
    legendItems.forEach((el, index) => {
      const onEnter = () => {
        if (!canHover) return;
        hoverIndex = index;
        applyState();
      };
      const onLeave = () => {
        if (hoverIndex === index) {
          hoverIndex = null;
          applyState();
        }
      };
      // Fires on open/close of a <details>; keeps the arc dim/highlight in sync.
      const onToggle = () => applyState();
      // Touch tap handler.
      // - Plain <div> legend item (no `[open]` state): toggle its selection (tap again to
      //   clear); multiple may be selected.
      // - <details> item: some touch engines (older iOS Safari / Android WebView) don't
      //   fire the native `toggle` event, so `onToggle` never runs. The native open state
      //   still flips on tap, so re-sync on the next frame after the browser applies it.
      //   When `toggle` does fire this is a harmless idempotent re-run.
      const onClick = () => {
        if (canHover) return;
        if (el.tagName === 'DETAILS') {
          requestAnimationFrame(applyState);
          return;
        }
        if (selectedIndices.has(index)) selectedIndices.delete(index);
        else selectedIndices.add(index);
        applyState();
      };

      el.addEventListener('mouseenter', onEnter);
      el.addEventListener('mouseleave', onLeave);
      el.addEventListener('toggle', onToggle);
      el.addEventListener('click', onClick);
      legendCleanups.push(() => {
        el.removeEventListener('mouseenter', onEnter);
        el.removeEventListener('mouseleave', onLeave);
        el.removeEventListener('toggle', onToggle);
        el.removeEventListener('click', onClick);
      });
    });

    /** Only react to mutations whose subtree contains pie arcs (recharts renders async). */
    function mutationTouchesPieArcs(mutation: MutationRecord): boolean {
      if (mutation.type !== 'childList') return false;
      const subtreeHasArc = (root: Node): boolean => {
        if (root.nodeType !== Node.ELEMENT_NODE) return false;
        const el = root as Element;
        return el.matches(PIE_ARC_SELECTOR) || el.querySelector(PIE_ARC_SELECTOR) != null;
      };
      return (
        Array.from(mutation.addedNodes).some(subtreeHasArc) ||
        Array.from(mutation.removedNodes).some(subtreeHasArc)
      );
    }

    setupArcs();

    const observer = new MutationObserver(mutations => {
      if (mutations.some(mutationTouchesPieArcs)) setupArcs();
    });
    observer.observe(container, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      teardownArcs();
      legendCleanups.forEach(fn => fn());
    };
  }, [containerEl, items.length]);

  const pieData = useMemo(
    () =>
      items.map((item, i) => ({
        ...item,
        fill: item.color ?? DEFAULT_COLORS[i],
      })),
    [items],
  );

  return (
    <ChartWrapper>
      <div
        ref={setContainerRef}
        id={CONTAINER_ID}
        className={cx(
          css({
            width: 'full',
            display: 'flex',
            flexDirection: { base: 'column-reverse', lg: 'row' },
            columnGap: '8',
            rowGap: '8',
          }),
          chartInteractionStyles,
        )}
      >
        <ChartLegend data={items} />
        <div
          className={css({
            display: 'flex',
            justifyContent: 'center',
            flexGrow: '[40]',
            flexShrink: '1',
            flexBasis: '[0%]',
          })}
        >
          <div
            className={css({
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
              width: '[292px]',
              height: '[292px]',
              '& .recharts-sector:focus': {
                outlineWidth: 'none',
                outlineStyle: 'none',
                outlineColor: 'transparent',
              },
              '& .recharts-surface:focus': {
                outlineWidth: 'none',
                outlineStyle: 'none',
                outlineColor: 'transparent',
              },
            })}
          >
            <div
              className={css({
                position: 'absolute',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                columnGap: '0.5',
                rowGap: '0.5',
              })}
            >
              <span
                className={css({
                  fontSize: 'xxl',
                  color: 'neutral.darkest',
                  fontWeight: 'bold',
                  lineHeight: 'normal',
                })}
              >
                {overallRating?.toFixed(1)}
              </span>
              <Rating
                value={overallRating}
                size="15px"
                gap="6px"
                color="#8DC63F"
                additionalStyles={css({ marginRight: '0.5' })}
              />
            </div>
            <CategoriesPieChart pieData={pieData} />
          </div>
        </div>
      </div>
    </ChartWrapper>
  );
}
